import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createApiClient } from '../client';
import { useAuth } from '../../auth/useAuth';

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export interface BeatDto {
  id: string;
  storyArchitectSessionId: string;
  name: string;
  beatType?: string | null;
  description?: string | null;
  sequenceOrder: number;
  linkedParagraphIds: string[];
  synopsis?: string | null;
  location?: string | null;
  timePeriod?: string | null;
  mood?: string | null;
  tone?: string | null;
  emotionalIntensity?: number | null;
  emotionalDirection?: string | null;
  notes?: string | null;
  linkedOutlineNodeId?: string | null;
}

export interface StoryArchitectSessionDto {
  id: string;
  projectId?: string | null;
  name?: string | null;
  synopsis?: string | null;
  beatsCount: number;
  beats: BeatDto[];
  updatedAt: string;
}

export interface StoryStructureDto {
  id: string;
  name: string;
  description?: string | null;
  phases: unknown;
}

export function useStoryStructures() {
  const { getToken } = useAuth();
  return useQuery<StoryStructureDto[]>({
    queryKey: ['story-structures'],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const response = await client
        .get('api/v2/story-architect/structures')
        .json<ApiResponse<StoryStructureDto[]>>();
      return response.data ?? [];
    },
  });
}

export function useStoryArchitectSession(projectId: string | undefined) {
  const { getToken } = useAuth();
  return useQuery<StoryArchitectSessionDto | null>({
    enabled: !!projectId,
    queryKey: ['story-architect', projectId],
    queryFn: async () => {
      const client = createApiClient(getToken);
      try {
        const response = await client
          .get(`api/v2/projects/${projectId}/story-architect`)
          .json<ApiResponse<StoryArchitectSessionDto>>();
        return response.data;
      } catch (error) {
        // 404 means no session yet
        const asAny = error as { response?: { status?: number } };
        if (asAny.response?.status === 404) return null;
        throw error;
      }
    },
  });
}

export function useCreateSession(projectId: string | undefined) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      synopsis?: string;
      structureId?: string;
    }) => {
      const client = createApiClient(getToken);
      const response = await client
        .post('api/v2/story-architect/sessions', {
          json: {
            projectId: projectId ?? null,
            name: input.name,
            synopsis: input.synopsis,
            structureId: input.structureId,
          },
        })
        .json<ApiResponse<StoryArchitectSessionDto>>();
      return response.data;
    },
    onSuccess: () => {
      if (projectId) {
        void queryClient.invalidateQueries({
          queryKey: ['story-architect', projectId],
        });
      }
      void queryClient.invalidateQueries({
        queryKey: ['story-architect-sessions'],
      });
    },
  });
}

export function useCreateBeat(projectId: string | undefined) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      sessionId: string;
      name: string;
      beatType?: string;
      description?: string;
      sequenceOrder?: number;
    }) => {
      const client = createApiClient(getToken);
      const response = await client
        .post('api/v2/beats', { json: input })
        .json<ApiResponse<BeatDto>>();
      return response.data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['story-architect', projectId],
      }),
  });
}

export function useUpdateBeat(projectId: string | undefined) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      name: string;
      beatType?: string;
      description?: string;
      synopsis?: string | null;
      location?: string | null;
      timePeriod?: string | null;
      mood?: string | null;
      tone?: string | null;
      emotionalIntensity?: number | null;
      emotionalDirection?: string | null;
      notes?: string | null;
    }) => {
      const { id, ...body } = input;
      const client = createApiClient(getToken);
      await client.put(`api/v2/beats/${id}`, { json: body });
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['story-architect', projectId],
      }),
  });
}

export function useReorderBeat(projectId: string | undefined) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      newSequenceOrder,
    }: {
      id: string;
      newSequenceOrder: number;
    }) => {
      const client = createApiClient(getToken);
      await client.put(`api/v2/beats/${id}/reorder`, {
        json: { newSequenceOrder },
      });
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['story-architect', projectId],
      }),
  });
}

export function useMapBeatRange(projectId: string | undefined) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      beatId: string;
      chapterId: string;
      paragraphIds: string[];
    }) => {
      const client = createApiClient(getToken);
      await client.put(`api/v2/beats/${input.beatId}/map-range`, {
        json: { chapterId: input.chapterId, paragraphIds: input.paragraphIds },
      });
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['story-architect', projectId],
      }),
  });
}

export function useDeleteBeat(projectId: string | undefined) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const client = createApiClient(getToken);
      await client.delete(`api/v2/beats/${id}`);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['story-architect', projectId],
      }),
  });
}

export function useStoryArchitectSessionById(sessionId: string | undefined) {
  const { getToken } = useAuth();
  return useQuery<StoryArchitectSessionDto | null>({
    enabled: !!sessionId,
    queryKey: ['story-architect-session', sessionId],
    queryFn: async () => {
      const client = createApiClient(getToken);
      try {
        const response = await client
          .get(`api/v2/story-architect/sessions/${sessionId}`)
          .json<ApiResponse<StoryArchitectSessionDto>>();
        return response.data;
      } catch (error) {
        const asAny = error as { response?: { status?: number } };
        if (asAny.response?.status === 404) return null;
        throw error;
      }
    },
  });
}

export function useCreateBeatForSession(sessionId: string | undefined) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      sessionId: string;
      name: string;
      beatType?: string;
      description?: string;
      sequenceOrder?: number;
    }) => {
      const client = createApiClient(getToken);
      const response = await client
        .post('api/v2/beats', { json: input })
        .json<ApiResponse<BeatDto>>();
      return response.data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['story-architect-session', sessionId],
      }),
  });
}

export function useUpdateBeatForSession(sessionId: string | undefined) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      name: string;
      beatType?: string;
      description?: string;
      synopsis?: string | null;
      location?: string | null;
      timePeriod?: string | null;
      mood?: string | null;
      tone?: string | null;
      emotionalIntensity?: number | null;
      emotionalDirection?: string | null;
      notes?: string | null;
    }) => {
      const { id, ...body } = input;
      const client = createApiClient(getToken);
      await client.put(`api/v2/beats/${id}`, { json: body });
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['story-architect-session', sessionId],
      }),
  });
}

export function useReorderBeatForSession(sessionId: string | undefined) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      newSequenceOrder,
    }: {
      id: string;
      newSequenceOrder: number;
    }) => {
      const client = createApiClient(getToken);
      await client.put(`api/v2/beats/${id}/reorder`, {
        json: { newSequenceOrder },
      });
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['story-architect-session', sessionId],
      }),
  });
}

export function useDeleteBeatForSession(sessionId: string | undefined) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const client = createApiClient(getToken);
      await client.delete(`api/v2/beats/${id}`);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['story-architect-session', sessionId],
      }),
  });
}

export function useDeleteSession() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const client = createApiClient(getToken);
      await client.delete(`api/v2/story-architect/sessions/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['story-architect-sessions'] });
    },
  });
}

// --- Standalone listing ---

export interface StoryArchitectSessionSummaryDto {
  id: string;
  projectId?: string | null;
  projectTitle?: string | null;
  name: string;
  beatsCount: number;
  updatedAt: string;
}

export function useMyStoryArchitectSessions() {
  const { getToken } = useAuth();
  return useQuery<StoryArchitectSessionSummaryDto[]>({
    queryKey: ['story-architect-sessions'],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const res = await client
        .get('api/v2/story-architect/sessions')
        .json<ApiResponse<StoryArchitectSessionSummaryDto[]>>();
      return res.data ?? [];
    },
  });
}

// --- Session characters ---

export interface StoryArchitectCharacterDto {
  id: string;
  sessionId: string;
  entityId: string;
  entityName?: string | null;
  role?: string | null;
  customRoleName?: string | null;
  goal?: string | null;
  goalType?: string | null;
  motivation?: string | null;
  stakes?: string | null;
  arcSummary?: string | null;
  sequenceOrder: number;
}

export function useSessionCharacters(sessionId: string | undefined) {
  const { getToken } = useAuth();
  return useQuery<StoryArchitectCharacterDto[]>({
    enabled: !!sessionId,
    queryKey: ['sa-characters', sessionId],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const resp = await client
        .get(`api/v2/story-architect/sessions/${sessionId}/characters`)
        .json<ApiResponse<StoryArchitectCharacterDto[]>>();
      return resp.data ?? [];
    },
  });
}

export function useAddSessionCharacter(sessionId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      entityId: string;
      role?: string | null;
      customRoleName?: string | null;
      goal?: string | null;
      goalType?: string | null;
      motivation?: string | null;
      stakes?: string | null;
      arcSummary?: string | null;
    }) => {
      const client = createApiClient(getToken);
      const resp = await client
        .post(`api/v2/story-architect/sessions/${sessionId}/characters`, {
          json: input,
        })
        .json<ApiResponse<StoryArchitectCharacterDto>>();
      return resp.data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['sa-characters', sessionId] }),
  });
}

export function useRemoveSessionCharacter(sessionId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const client = createApiClient(getToken);
      await client.delete(`api/v2/story-architect-characters/${id}`);
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['sa-characters', sessionId] }),
  });
}
